/**
 * グローバルキーボードショートカット。
 *
 * 設計書 FR-17: `SPACE` で DETONATE、`R` で Reset。
 *
 * タイピング中の干渉を避けるため、フォーカスが `<input>` / `<textarea>` /
 * `contenteditable` 要素にある場合はショートカットを発火しない。
 * `<input type="range">` も INPUT タグだが、方向キーでの操作時に SPACE が
 * 誤爆するケースを避けるため、タイプに関わらず INPUT 全般を無視する。
 */

import type { AppState, Store } from '../state';

/** `mountKeyboard` の handler 集合。 */
export interface KeyboardHandlers {
  /** SPACE 押下時に呼ばれる。通常は `timeline.onDetonate` と同じ callback。 */
  onDetonate: () => void;
}

/**
 * `document` に keydown リスナを登録する。
 *
 * @param store - グローバルストア（R で progress/isAnimating をリセット）
 * @param handlers - onDetonate
 * @returns teardown 関数（リスナ解除）
 */
export function mountKeyboard(store: Store<AppState>, handlers: KeyboardHandlers): () => void {
  const onKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
    }

    if (e.code === 'Space') {
      // ページスクロール防止
      e.preventDefault();
      handlers.onDetonate();
      return;
    }

    if (e.key === 'r' || e.key === 'R') {
      store.set({ progress: 0, isAnimating: false });
    }
  };

  document.addEventListener('keydown', onKeyDown);

  return (): void => {
    document.removeEventListener('keydown', onKeyDown);
  };
}
